/**
 * Directory list-row geometry.
 *
 * The row used to be a flex chain, which is why nothing lined up column to
 * column and why the hover actions — always in the DOM at opacity 0 — held a
 * wide slot open in the middle of every row at rest. It is now a CSS grid with
 * fixed tracks, so the action cell is explicitly reserved instead of being an
 * accident of flex distribution.
 *
 * Column order (and therefore DOM order) is:
 *   avatar · name+title · tenure · department · office · spacer · actions
 *
 * Track counts must match the number of *visible* children at each breakpoint,
 * because a child hidden with `display: none` does not occupy a grid cell.
 *   base : avatar · name · actions                                   (3)
 *   md   : avatar · name · tenure · department · spacer · actions     (6)
 *   lg   : + office                                                   (7)
 *
 * Call, Message and Favorite live in one "actions" cell, always visible at
 * every breakpoint — they used to be hover-revealed on desktop, which hid
 * their existence from anyone who hadn't discovered the hover affordance.
 *
 * WHY THE SPACER: the text columns are capped, and ALL leftover width pools in
 * one flexible spacer immediately before the actions. Two earlier attempts were
 * worse — giving the name column every spare pixel left an ~800px hole between
 * the job title and the tenure chip at 1440px, and sharing slack proportionally
 * between name/department/office just spread that hole across three columns
 * (~500px of nothing at 1900px). Capped columns plus one spacer keeps the data
 * clustered left and the actions pinned right, which is what makes a table read
 * as dense. Fixed and capped tracks are identical on every row, so columns still
 * align exactly.
 *
 * The text columns need a FLOOR as well as a ceiling. With `minmax(0, 420px)`
 * the `1fr` spacer consumed all free space first and the capped tracks fell back
 * to their 0 minimum — their children carry `min-w-0` + `truncate`, so
 * min-content is 0 and every cell collapsed. That is what truncated
 * "VP of Mechanical Engineering" mid-word even though the ceiling was 420px.
 *
 * The class strings below are written out in full rather than composed at
 * runtime so Tailwind's scanner can see them (content globs cover src/**\/*.js).
 */

// Call, Message and Favorite are now one always-visible labeled group (not
// three icon-only buttons revealed on hover). Labels only fit once the row
// has real desktop width (lg+), so the cell is narrower and icon-only below
// that — see the `xl:inline`/`lg:inline` label spans in EmployeeGrid.jsx,
// which must switch at the same breakpoint this cell widens at.
export const ACTION_CELL_WIDTH = 260;

export const DENSITIES = {
  comfortable: {
    id: 'comfortable',
    label: 'Comfortable',
    rowHeight: 52,
    rowHeightClass: 'h-[52px]',
    avatarPx: 36,
    avatarClass: 'w-9 h-9',
    // Two lines: name over title
    stacked: true,
    gridClass:
      'grid-cols-[36px_minmax(0,1fr)_100px] md:grid-cols-[36px_minmax(220px,360px)_140px_minmax(130px,220px)_minmax(0,1fr)_130px] lg:grid-cols-[36px_minmax(260px,420px)_140px_minmax(150px,240px)_minmax(110px,200px)_minmax(0,1fr)_260px]',
  },
  compact: {
    id: 'compact',
    label: 'Compact',
    rowHeight: 40,
    rowHeightClass: 'h-[40px]',
    avatarPx: 28,
    avatarClass: 'w-7 h-7',
    // One line: name and title share it, so 40px stays honest
    stacked: false,
    gridClass:
      'grid-cols-[28px_minmax(0,1fr)_100px] md:grid-cols-[28px_minmax(220px,360px)_140px_minmax(130px,220px)_minmax(0,1fr)_130px] lg:grid-cols-[28px_minmax(260px,420px)_140px_minmax(150px,240px)_minmax(110px,200px)_minmax(0,1fr)_260px]',
  },
};

export const DEFAULT_DENSITY = 'comfortable';

const DENSITY_STORAGE_KEY = 'peoplevue_directory_density';

export function getDensity(id) {
  return DENSITIES[id] || DENSITIES[DEFAULT_DENSITY];
}

/** Density is a per-device display preference, so it lives in localStorage. */
export function loadDensity() {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    return DENSITIES[stored] ? stored : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

export function saveDensity(id) {
  try {
    if (DENSITIES[id]) localStorage.setItem(DENSITY_STORAGE_KEY, id);
  } catch {
    // A private-mode storage failure must not break the toggle
  }
}

/**
 * Tenure chip label. Deliberately a numeral, not a picture: the old row stacked
 * up to three emoji tier badges, which read as decoration and rendered
 * differently on every OS.
 */
export function tenureChipLabel(years) {
  if (years === null || years === undefined) return null;
  // "0y" reads as an error — as if the field failed to populate. Someone in
  // their first year has a real, meaningful tenure; it is just under a year.
  if (years < 1) return '<1y';
  return `${years}y`;
}
